import React, { useEffect, useMemo, useRef, useState } from 'react';
import '@/components/charts/charts.css';

const SEV = ['Critical', 'High', 'Medium', 'Low'];
const COLOR = { Critical: 'var(--state-critical)', High: 'var(--state-high)', Medium: 'var(--state-medium)', Low: 'var(--state-low)' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayLabel = (iso) => { const [, m, d] = iso.split('-'); return `${Number(d)} ${MONTHS[Number(m) - 1]}`; };

function niceMax(v) {
  if (v <= 4) return 4;
  const step = 10 ** Math.floor(Math.log10(v));
  const n = Math.ceil(v / step);
  return (n <= 2 ? 2 : n <= 5 ? 5 : 10) * step;
}

/**
 * New items per day, stacked by severity (Critical at the base so it lines up
 * across days). Hover or focus a day for its breakdown; click it to open the
 * queue for that day. Values come with a text summary for screen readers, and
 * every severity is named in the legend, so colour never carries meaning alone.
 *
 *   series: [{ date: 'YYYY-MM-DD', total, Critical, High, Medium, Low }]
 */
export default function DailyBars({ series, height = 180, onSelectDay, label = 'New threats per day', activeDay }) {
  const wrap = useRef(null);
  const [hover, setHover] = useState(null);
  // Drawn at the container's real width so axis text stays at its true size.
  const [width, setWidth] = useState(720);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(240, Math.round(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const max = useMemo(() => niceMax(Math.max(1, ...series.map((d) => d.total))), [series]);
  const ticks = [0, max / 2, max];
  const totals = SEV.map((s) => [s, series.reduce((a, d) => a + (d[s] || 0), 0)]);
  const busiest = series.reduce((a, d) => (d.total > a.total ? d : a), series[0] || { total: 0 });
  const every = Math.max(1, Math.ceil(series.length / 7));
  const W = width; const H = height; const L = 34; const B = 24; const T = 8;
  const slot = (W - L) / Math.max(series.length, 1);
  const bw = Math.min(slot * 0.62, 34);
  const y = (v) => H - B - (v / max) * (H - B - T);

  return (
    <figure className="ch" ref={wrap}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${label}: ${series.reduce((a, d) => a + d.total, 0)} over ${series.length} days. Busiest ${busiest.date ? `${dayLabel(busiest.date)} with ${busiest.total}` : 'none'}.`} style={{ height }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W} y1={y(t) + 0.5} y2={y(t) + 0.5} className="ch-grid" />
            <text x={L - 8} y={y(t) + 4} textAnchor="end" className="ch-axis">{t}</text>
          </g>
        ))}
        {series.map((d, i) => {
          const x = L + i * slot + (slot - bw) / 2;
          let top = H - B;
          const active = activeDay === d.date || hover?.date === d.date;
          return (
            <g
              key={d.date}
              className={`ch-col ${active ? 'is-active' : ''} ${d.total && onSelectDay ? 'is-clickable' : ''}`}
              onMouseMove={(e) => {
                const r = wrap.current.getBoundingClientRect();
                setHover({ ...d, x: e.clientX - r.left, y: e.clientY - r.top });
              }}
              onMouseLeave={() => setHover(null)}
              onClick={() => d.total && onSelectDay?.(d.date)}
              tabIndex={d.total && onSelectDay ? 0 : -1}
              role={d.total && onSelectDay ? 'button' : undefined}
              aria-label={`${dayLabel(d.date)}: ${d.total} threats${d.total ? `, ${SEV.filter((s) => d[s]).map((s) => `${d[s]} ${s}`).join(', ')}` : ''}`}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && d.total) { e.preventDefault(); onSelectDay?.(d.date); } }}
              onFocus={() => setHover({ ...d, x: L + i * slot + slot / 2, y: 20 })}
              onBlur={() => setHover(null)}
            >
              <rect x={L + i * slot} y={T} width={slot} height={H - B - T} className="ch-hit" />
              {SEV.map((s) => {
                if (!d[s]) return null;
                const h = Math.max(2, (d[s] / max) * (H - B - T));
                top -= h;
                return <rect key={s} x={x} y={top} width={bw} height={h} fill={COLOR[s]} className="ch-bar" />;
              })}
              {(i % every === (series.length - 1) % every) && <text x={x + bw / 2} y={H - 6} textAnchor="middle" className="ch-axis">{i === series.length - 1 ? 'Today' : dayLabel(d.date)}</text>}
            </g>
          );
        })}
      </svg>
      {hover && (
        <div className="ch-tip" style={{ left: Math.min(hover.x + 14, (wrap.current?.clientWidth || 0) - 190), top: Math.max(0, hover.y - 10) }} role="presentation">
          <b>{dayLabel(hover.date)}</b>
          <span>{hover.total} threat{hover.total === 1 ? '' : 's'}</span>
          {SEV.filter((s) => hover[s]).map((s) => <span key={s} className="ch-tip-row"><i style={{ background: COLOR[s] }} />{hover[s]} {s}</span>)}
          {hover.total > 0 && onSelectDay && <span className="ch-tip-hint">Click to open the queue for this day</span>}
        </div>
      )}
      <figcaption className="ch-legend">
        {totals.map(([s, n]) => <span key={s}><i style={{ background: COLOR[s] }} aria-hidden="true" />{s} <b>{n}</b></span>)}
      </figcaption>
    </figure>
  );
}
